/**
 * React Query hooks for LPA Schedule Instances.
 *
 * Instances are materialized audit period rows — the concrete "you need to do
 * this audit on this date" entries. They are frozen at generation time so
 * schedule changes don't retroactively affect them.
 *
 * Phase 3: Manual generation (one instance at a time per schedule).
 * Queries fetch instances by audit, schedule, or auditor.
 * Mutations: generate, update status, reassign auditor, delete.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/core/lib/queryKeys';
import { useTenant } from '@/core/hooks/useTenant';
import { useToast } from '@/shared/ui/use-toast';
import { useLanguage } from '@/core/LanguageContext';
import {
  fetchInstancesByAuditId,
  fetchInstancesByScheduleId,
  fetchInstancesByAuditorId,
  fetchInstanceById,
  generateInstance,
  updateInstanceStatus,
  updateInstanceAuditor,
  deleteInstance,
} from '../api/scheduleInstances';
import type {
  GenerateInstanceInput,
  UpdateInstanceStatusInput,
  UpdateInstanceAuditorInput,
} from '../types';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Fetch all instances for an audit (across all schedules). */
export function useInstancesByAuditId(
  auditId: string | null | undefined,
  dateRange?: { from?: string; to?: string },
) {
  const { selectedTenantId } = useTenant();

  return useQuery({
    queryKey: queryKeys.modLpaInstances(auditId ?? null, selectedTenantId),
    queryFn: () =>
      fetchInstancesByAuditId(auditId!, dateRange, selectedTenantId ?? undefined),
    enabled: !!auditId && !!selectedTenantId,
  });
}

/** Fetch instances for a specific schedule. */
export function useInstancesByScheduleId(scheduleId: string | null | undefined) {
  const { selectedTenantId } = useTenant();

  return useQuery({
    queryKey: queryKeys.modLpaScheduleInstances(scheduleId ?? null, selectedTenantId),
    queryFn: () => fetchInstancesByScheduleId(scheduleId!, selectedTenantId ?? undefined),
    enabled: !!scheduleId && !!selectedTenantId,
  });
}

/** Fetch instances assigned to a specific auditor (for Phase 4 "My Audits"). */
export function useInstancesByAuditorId(
  auditorId: string | null | undefined,
  filters?: { statuses?: string[]; from?: string; to?: string },
) {
  const { selectedTenantId } = useTenant();

  return useQuery({
    queryKey: queryKeys.modLpaAuditorInstances(auditorId ?? null, selectedTenantId, filters),
    queryFn: () =>
      fetchInstancesByAuditorId(auditorId!, filters, selectedTenantId ?? undefined),
    enabled: !!auditorId && !!selectedTenantId,
  });
}

/** Fetch a single instance by ID with full details. */
export function useInstanceById(instanceId: string | null | undefined) {
  const { selectedTenantId } = useTenant();

  return useQuery({
    queryKey: queryKeys.modLpaInstanceDetail(instanceId ?? null, selectedTenantId),
    queryFn: () => fetchInstanceById(instanceId!, selectedTenantId ?? undefined),
    enabled: !!instanceId && !!selectedTenantId,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** Generate (create) a single instance for a schedule. */
export function useGenerateInstance() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (
      input: Omit<GenerateInstanceInput, 'tenant_id'> & { auditId: string },
    ) =>
      generateInstance({
        ...input,
        tenant_id: selectedTenantId!,
      }),
    onSuccess: (_data, variables) => {
      // Refresh instance lists
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaInstances(variables.auditId, selectedTenantId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaScheduleInstances(variables.schedule_id, selectedTenantId),
      });
      toast({
        title: t('mod_lpa.instances.generateSuccess', 'Instance generated'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.instances.generateError', 'Failed to generate instance'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/** Update an instance's status (skip, cancel, etc.). */
export function useUpdateInstanceStatus() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({
      instanceId,
      input,
    }: {
      instanceId: string;
      auditId: string;
      scheduleId: string;
      input: UpdateInstanceStatusInput;
    }) => updateInstanceStatus(instanceId, input, selectedTenantId ?? undefined),
    onSuccess: (_data, { instanceId, auditId, scheduleId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaInstances(auditId, selectedTenantId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaScheduleInstances(scheduleId, selectedTenantId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaInstanceDetail(instanceId, selectedTenantId),
      });
      toast({
        title: t('mod_lpa.instances.statusUpdateSuccess', 'Instance status updated'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.instances.statusUpdateError', 'Failed to update status'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/** Reassign an instance to a different auditor (vacation substitution). */
export function useUpdateInstanceAuditor() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({
      instanceId,
      input,
    }: {
      instanceId: string;
      auditId: string;
      scheduleId: string;
      input: UpdateInstanceAuditorInput;
    }) => updateInstanceAuditor(instanceId, input, selectedTenantId ?? undefined),
    onSuccess: (_data, { instanceId, auditId, scheduleId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaInstances(auditId, selectedTenantId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaScheduleInstances(scheduleId, selectedTenantId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaInstanceDetail(instanceId, selectedTenantId),
      });
      toast({
        title: t('mod_lpa.instances.auditorUpdateSuccess', 'Auditor reassigned'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.instances.auditorUpdateError', 'Failed to reassign auditor'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/** Delete a pending instance. */
export function useDeleteInstance() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({
      instanceId,
    }: {
      instanceId: string;
      auditId: string;
      scheduleId: string;
    }) => deleteInstance(instanceId, selectedTenantId ?? undefined),
    onSuccess: (_data, { auditId, scheduleId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaInstances(auditId, selectedTenantId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaScheduleInstances(scheduleId, selectedTenantId),
      });
      toast({
        title: t('mod_lpa.instances.deleteSuccess', 'Instance deleted'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.instances.deleteError', 'Failed to delete instance'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
